import MonacoEditor, { OnMount } from '@monaco-editor/react';
import {
  CUSTOM_OPERATORS_ENUM,
  TCondition,
  TFormField,
  UTILITY_OPERATORS_ENUM,
} from '@nirmata/shared';
import { toast } from '@nirmata/web-components';
import { Button } from '@nirmata/web-components/ui/button';
import { Separator } from '@nirmata/web-components/ui/separator';
import { Save } from 'lucide-react';
import * as monaco from 'monaco-editor';
import { useEffect, useRef, useState } from 'react';

import { NIRMATA_DARK_THEME, NIRMATA_LIGHT_THEME } from '@/components/theme/monacoThemes';
import { useTheme } from '@/components/theme/ThemeProvider';
import { conditionalRulesSchema } from '@/schemas/conditional-rules-schema';
import { useBuilderStore } from '@/store/builder';

interface ConditionalRulesEditorProps {
  field: TFormField;
  onChange: (updates: Partial<TFormField>) => void;
}

let nirmataThemesDefined = false;

export function ConditionalRulesEditor({ field, onChange }: ConditionalRulesEditorProps) {
  const { theme } = useTheme();
  const monacoRef = useRef<typeof monaco | null>(null);
  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);
  const [draftRules, setDraftRules] = useState<TCondition[]>(field.conditionalRules ?? []);

  // Configure Monaco JSON language features
  const setLanguageFeatures = (monacoInstance: typeof monaco) => {
    // Dynamically patch the schema for field name suggestions
    const fieldNames = useBuilderStore.getState().fieldNames;
    const dynamicSchema = JSON.parse(JSON.stringify(conditionalRulesSchema));
    const patchArrayEnumForOperator = (operator: string) => {
      if (dynamicSchema?.$defs?.condition?.properties?.[operator]) {
        if (operator === UTILITY_OPERATORS_ENUM.onField) {
          // For onField, use anyOf for string (with suggestions) or array (generic)
          dynamicSchema.$defs.condition.properties[operator] = {
            anyOf: [
              { type: 'string', enum: fieldNames },
              { type: 'array', items: { $ref: '#/$defs/conditionOrValue' } },
            ],
          };
        } else {
          // Default: array of string suggestions (as before)
          dynamicSchema.$defs.condition.properties[operator] = {
            type: 'array',
            items: [{ type: 'string', enum: fieldNames }, { $ref: '#/$defs/conditionOrValue' }],
          };
        }
      }
    };
    patchArrayEnumForOperator(CUSTOM_OPERATORS_ENUM.valueOfField);
    patchArrayEnumForOperator(UTILITY_OPERATORS_ENUM.onField);

    // Configure JSON language features with schema validation
    monacoInstance.languages.json.jsonDefaults.setDiagnosticsOptions({
      validate: true,
      allowComments: false,
      trailingCommas: 'error',
      schemaValidation: 'error',
      schemas: [
        {
          uri: 'nirmata://conditional-rules.json', // Match model URI
          fileMatch: ['nirmata://conditional-rules.json'],
          schema: dynamicSchema,
        },
      ],
    });
  };

  const handleEditorDidMount: OnMount = (editor, monacoInstance) => {
    editorRef.current = editor;
    monacoRef.current = monacoInstance;

    // Ensure model uses the custom URI for schema association
    const CONDITIONAL_RULES_URI = 'nirmata://conditional-rules.json';
    const modelUri = monacoInstance.Uri.parse(CONDITIONAL_RULES_URI);
    let model = monacoInstance.editor.getModel(modelUri);
    if (!model) {
      model = monacoInstance.editor.createModel(
        JSON.stringify(draftRules ?? [], null, 2),
        'json',
        modelUri
      );
    }
    editor.setModel(model);

    // Define custom themes if not already defined
    if (monacoInstance && monacoInstance.editor) {
      if (!nirmataThemesDefined) {
        monacoInstance.editor.defineTheme('nirmata-light', {
          ...NIRMATA_LIGHT_THEME,
          base: 'vs' as const,
        });
        monacoInstance.editor.defineTheme('nirmata-dark', {
          ...NIRMATA_DARK_THEME,
          base: 'vs-dark' as const,
        });
        nirmataThemesDefined = true;
      }
      monacoInstance.editor.setTheme(theme === 'dark' ? 'nirmata-dark' : 'nirmata-light');
      setLanguageFeatures(monacoInstance);
    }
  };

  useEffect(() => {
    if (monacoRef.current && monacoRef.current.editor) {
      monacoRef.current.editor.setTheme(theme === 'dark' ? 'nirmata-dark' : 'nirmata-light');
    }
  }, [theme]);

  const handleSave = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    let value = '[]';
    if (editorRef.current?.getValue) {
      value = editorRef.current.getValue();
    }
    try {
      const parsed = JSON.parse(value);
      setDraftRules(parsed);
      onChange({ conditionalRules: parsed });
      console.log('Saving conditional rules:', parsed);
      toast.success('Conditional rules saved successfully!');
    } catch (error) {
      toast.error('Invalid JSON');
    }
  };

  return (
    <div className="w-full">
      <div className="py-4">
        <div className="flex items-center justify-between mb-2">
          <span className="font-semibold text-base">Conditional Rules</span>
        </div>
        <Separator className="mb-4" />
        <span style={{ fontFamily: 'Fira Code' }}>
          <MonacoEditor
            height="300px"
            language="json"
            onMount={handleEditorDidMount}
            options={{
              minimap: { enabled: true },
              fontSize: 16,
              fontFamily: 'Arial',
              fontLigatures: true,
              formatOnPaste: true,
              formatOnType: true,
              scrollBeyondLastLine: false,
              theme: theme === 'dark' ? 'nirmata-dark' : 'nirmata-light',
              suggestOnTriggerCharacters: true,
              quickSuggestions: {
                other: true,
                comments: false,
                strings: true,
              },
              acceptSuggestionOnEnter: 'on',
              autoClosingBrackets: 'always',
              autoClosingQuotes: 'always',
              tabCompletion: 'on',
              snippetSuggestions: 'bottom',
              wordBasedSuggestions: 'off',
            }}
          />
        </span>
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <Button
          className="cursor-pointer"
          variant="outline"
          type="button"
          onClick={() => setDraftRules(draftRules)}
        >
          Reset
        </Button>
        <Button type="button" className="cursor-pointer" onClick={handleSave}>
          <Save className="h-4 w-4 mr-2" />
          Save Conditional Rules
        </Button>
      </div>
    </div>
  );
}
